import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';

/**
 * 四个金额卡（UI 规范 554 行：毛额 / 已退 / 佣金 / **应付净额**）。
 *
 * **本组件不做任何减法**：`netSettlement` 用服务端字段；`totalSales` 是**毛额**口径
 * （附录 A-P7-03：不扣退款），前端若做 `totalSales − refundDeduction − commission`
 * 就会在「退款发生在结算后」（反向单）这类场景下与服务端分叉。
 *
 * props 用**结构化**形状（四个字符串）而不是整份 `SettlementView`：详情与预览
 * （`PendingSettlementLines`）都能直接喂进来 —— 这正是「预览与提交同一个口径」的前端一半。
 * 值读不出来时说 `金额以服务端为准`（`AMOUNT_UNAVAILABLE_TEXT`），绝不说成 `¥0.00`。
 */
interface Props {
  totalSales: string | null | undefined;
  refundDeduction: string | null | undefined;
  commission: string | null | undefined;
  netSettlement: string | null | undefined;
}

/**
 * 服务端字符串 → 展示串；**读不出来返回占位文案**（不是 `¥0.00`）。
 *
 * `formatServerYuan` 只接受 `string | number`，因此空值在这里先收口 —— 直接
 * `formatServerYuan(amount ?? null)` 会过不了类型检查（`null` 不是它的入参）。
 */
function amountText(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return AMOUNT_UNAVAILABLE_TEXT;
  return formatServerYuan(amount) ?? AMOUNT_UNAVAILABLE_TEXT;
}

function Card({
  title,
  amount,
  hint,
  valueTestId,
}: {
  title: string;
  amount: string | null | undefined;
  hint: string;
  valueTestId: string;
}) {
  return (
    <article className="flex flex-col gap-1 rounded-lg border-2 border-black p-4">
      <span className="text-sm text-gray-500">{title}</span>
      <span className="text-xl font-bold" data-testid={valueTestId}>
        {amountText(amount)}
      </span>
      <span className="text-xs text-gray-600">{hint}</span>
    </article>
  );
}

export default function SettlementSummaryCards({ totalSales, refundDeduction, commission, netSettlement }: Props) {
  return (
    <section className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Card title="毛额" amount={totalSales} hint="不扣退款" valueTestId="settlement-total-sales" />
      <Card title="已退" amount={refundDeduction} hint="退款毛额" valueTestId="settlement-refund-deduction" />
      <Card title="佣金" amount={commission} hint="条目上快照的费率" valueTestId="settlement-commission" />
      <Card title="应付净额" amount={netSettlement} hint="服务端下发" valueTestId="settlement-net" />
    </section>
  );
}
